import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, Globe } from "@phosphor-icons/react";
import { useLang } from "../../context/LanguageContext";
import { LOGO_URL } from "../../lib/assets";
import { scrollToId } from "../../hooks/useLenis";

const scrollTo = (id) => scrollToId(id);

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "why", label: t.nav.why },
    { id: "areas", label: t.nav.areas },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-forest/10 shadow-sm" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <button onClick={() => scrollTo("hero")} className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Kelsy Housecleaning" className="h-11 w-11 rounded-xl object-cover ring-1 ring-forest/10" />
          <span className="hidden sm:block leading-none">
            <span className="block font-serif text-xl text-forest tracking-tight">Kelsy Housecleaning</span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-leaf font-semibold">Ottawa · Gatineau</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-semibold text-forest/70 hover:text-forest transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggle}
            className="flex items-center gap-1.5 rounded-full border border-forest/20 px-3 py-2 text-xs font-bold text-forest hover:bg-forest hover:text-white transition-colors"
            data-testid="lang-toggle"
          >
            <Globe size={15} weight="bold" />
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <button
            onClick={() => scrollTo("quote")}
            className="hidden sm:inline-flex rounded-full bg-forest px-5 py-2.5 text-sm font-bold text-white hover:bg-forest-dark transition-colors"
            data-testid="nav-quote-cta"
          >
            {t.nav.quote}
          </button>
          <Link
            to="/admin"
            className="hidden md:inline-flex rounded-full border border-gold/40 px-4 py-2.5 text-sm font-bold text-gold hover:bg-gold hover:text-white transition-colors"
            data-testid="nav-admin-link"
          >
            {t.nav.admin}
          </Link>
          <button className="lg:hidden text-forest p-2" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle">
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-forest/10"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {[...links, { id: "quote", label: t.nav.quote }].map((l) => (
                <button
                  key={l.id}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="text-left text-base font-semibold text-forest"
                  data-testid={`nav-mobile-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <Link to="/admin" className="text-left text-base font-semibold text-gold" data-testid="nav-mobile-admin">
                {t.nav.admin}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
